/**
 * useTeachersFilter - Hook for filtering public teachers
 *
 * Search by first/last/preferred name; filter by work location
 */

import { useMemo, useState, useCallback } from "react";
import { useDebounce } from "@/hooks";
import type { GetTeacherResponse } from "@/openapi/generated";
import { filterPublicTeachers } from "./filterPublicTeachers";

export interface TeachersFilterInitialState {
  search?: string;
  locationFilter?: string | null;
}

export { filterPublicTeachers } from "./filterPublicTeachers";
export type { PublicTeachersFilterInput } from "./filterPublicTeachers";

export const useTeachersFilter = (
  teachers: GetTeacherResponse[] | undefined,
  initial?: TeachersFilterInitialState,
) => {
  const [search, setSearch] = useState(initial?.search ?? "");
  const debouncedSearch = useDebounce(search, 300);
  const [locationFilter, setLocationFilter] = useState<string | null>(
    initial?.locationFilter ?? null,
  );

  const filteredData = useMemo(
    () =>
      filterPublicTeachers(teachers, {
        search: debouncedSearch,
        location: locationFilter,
      }),
    [teachers, debouncedSearch, locationFilter],
  );

  const hasActiveFilters = useMemo(
    () => locationFilter !== null,
    [locationFilter],
  );

  const resetFilters = useCallback(() => {
    setLocationFilter(null);
  }, []);

  return {
    search,
    setSearch,
    locationFilter,
    setLocationFilter,
    filteredData,
    hasActiveFilters,
    isSearchPending: search !== debouncedSearch,
    resetFilters,
  };
};
